import { ProductWithCalculations } from '../shared/types/inventory';
import {CardHelp} from '../shared/components/CardHelp';

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 2});

interface MetricsPanelProps {
  product: ProductWithCalculations;
  leadTime: number;
  targetCoverage: number;
}

export const MetricsPanel = ({ product, leadTime, targetCoverage }: MetricsPanelProps) => {
  const serviceRate = product.quantidadePedida && product.quantidadeEntregue !== undefined
    ? (product.quantidadeEntregue / product.quantidadePedida) * 100
    : undefined;
  const periodMetrics = [
    serviceRate !== undefined && {label: 'Atendimento do pedido', value: `${serviceRate.toFixed(1)}%`},
    product.otifPct !== undefined && {label: 'OTIF', value: `${product.otifPct.toFixed(1)}%`},
    product.saidaPerdas !== undefined && {label: 'Saídas por perdas', value: `${NUMBER_FORMATTER.format(product.saidaPerdas)} un`},
    product.divergenciaEstoque !== undefined && {label: 'Divergência de estoque', value: `${NUMBER_FORMATTER.format(product.divergenciaEstoque)} un`},
  ].filter((metric): metric is {label: string; value: string} => Boolean(metric));
  const metrics = [
    {
      label: 'Estoque Atual',
      value: product.estoqueAtual.toFixed(0),
      unit: 'un',
      color: 'text-slate-900',
      bgColor: 'bg-slate-50',
      help: 'Soma disponível atualmente para este produto.',
    },
    {
      label: 'CMD',
      value: product.cmd.toFixed(2),
      unit: 'un/dia',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      help: 'Consumo médio diário = vendas do período ÷ quantidade de dias do período.',
    },
    {
      label: 'ROP',
      value: product.rop.toFixed(0),
      unit: 'un',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      help: 'Ponto de pedido = demanda durante o lead time (CMD × LT).',
    },
    {
      label: 'Estoque Alvo',
      value: product.estoqueAlvo.toFixed(0),
      unit: 'un',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      help: 'Estoque alvo = CMD × (lead time + dias de cobertura alvo).',
    },
    {
      label: 'Estoque de Segurança',
      value: product.ss.toFixed(0),
      unit: 'un',
      color: 'text-slate-700',
      bgColor: 'bg-slate-50',
      help: 'Reserva calculada para absorver variações e imprevistos.',
    },
    {
      label: 'Cobertura Atual',
      value: product.cobertura.toFixed(1),
      unit: 'dias',
      color: 'text-violet-700',
      bgColor: 'bg-violet-50',
      help: 'Cobertura atual = estoque atual ÷ consumo médio diário.',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg">
        <CardHelp content="Pedido sugerido = estoque alvo menos estoque atual, limitado ao mínimo de zero." className="absolute right-2 top-2 text-white/70 hover:bg-white/10 hover:text-white" />
        <div className="text-sm font-medium opacity-90 mb-1">Pedido Sugerido</div>
        <div className="text-4xl font-bold tracking-tight">{product.pedidoSugerido.toFixed(0)}</div>
        <div className="text-sm opacity-80 mt-1">unidades</div>
        <div className="text-xs opacity-75 mt-3 pt-3 border-t border-white/20">
          = Estoque Alvo - Estoque Atual
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className={`${metric.bgColor} relative rounded-xl border border-slate-200/50 p-4`}
          >
            <CardHelp content={metric.help} className="absolute right-2 top-2" />
            <div className="pr-7 text-xs font-medium text-slate-600 mb-1">{metric.label}</div>
            <div className="flex items-baseline gap-1">
              <span className={`text-2xl font-bold ${metric.color}`}>{metric.value}</span>
              <span className="text-xs text-slate-500">{metric.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-4">
        <CardHelp content="Parâmetros usados no cálculo deste produto. O lead time pode vir do fornecedor; a cobertura alvo vem das configurações." className="absolute right-2 top-2" />
        <div className="mb-3 pr-7 text-xs font-semibold text-slate-700">Configurações</div>
        <div className="space-y-2 text-xs text-slate-600">
          <div className="flex justify-between">
            <span>Lead Time (LT):</span>
            <span className="font-semibold text-slate-900">{leadTime} dias</span>
          </div>
          <div className="flex justify-between">
            <span>Cobertura Alvo:</span>
            <span className="font-semibold text-slate-900">{targetCoverage} dias</span>
          </div>
          <div className="flex justify-between">
            <span>Vendas no Período:</span>
            <span className="font-semibold text-slate-900">{product.vendasPeriodo} un</span>
          </div>
        </div>
      </div>

      {periodMetrics.length > 0 && (
        <div className="relative rounded-xl border border-teal-100 bg-teal-50/60 p-4">
          <CardHelp content="Indicadores trazidos pelos arquivos de vendas, entradas, estoque e OTIF. Eles aparecem apenas quando foram informados na importação." className="absolute right-2 top-2" />
          <div className="mb-3 pr-7 text-xs font-semibold text-teal-900">Desempenho do período</div>
          <div className="divide-y divide-teal-100 text-xs">
            {periodMetrics.map((metric) => (
              <div key={metric.label} className="flex items-start justify-between gap-4 py-2 first:pt-0 last:pb-0">
                <span className="text-slate-600">{metric.label}</span>
                <span className="text-right font-semibold tabular-nums text-slate-900">{metric.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
